(() => {
  'use strict';
  if (customElements.get('bp-world-navigation')) return;
  class BPWorldNavigation extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('dialog');
      this.worlds = [...this.querySelectorAll('details[data-world]')];
      this.abort = new AbortController();
      const signal = this.abort.signal;
      this.onClick = this.handleClick.bind(this);
      document.addEventListener('click', this.onClick, {capture: true, signal});
      document.addEventListener('keydown', event => {
        const trigger = event.target.closest('.wt-header__menu-trigger, .wt-header__sticky-menu-trigger');
        if (trigger && event.key === ' ' && trigger.tagName === 'A') {
          event.preventDefault(); this.open(trigger);
        }
        if (event.key === 'Escape') {
          this.escapeCancelSeen = false;
          this.escapeNested = [...document.querySelectorAll('dialog:modal')].some(dialog => dialog !== this.dialog);
          this.escapeComposing = event.isComposing;
        }
      }, {signal});
      this.dialog.addEventListener('cancel', event => {
        this.escapeCancelSeen = true;
        if (this.escapeNested) event.preventDefault();
      }, {signal});
      document.addEventListener('keyup', event => {
        if (event.key !== 'Escape') return;
        const nativeCancelSeen = this.escapeCancelSeen;
        const nestedEscape = this.escapeNested;
        const composingEscape = this.escapeComposing;
        this.escapeCancelSeen = false;
        this.escapeNested = false;
        this.escapeComposing = false;
        if (nativeCancelSeen || nestedEscape || composingEscape || event.isComposing || !this.dialog.open ||
            event.target.closest('dialog') !== this.dialog ||
            event.target.closest('input, textarea, select, [contenteditable], [role="combobox"], [role="listbox"]') ||
            [...document.querySelectorAll('dialog:modal')].some(dialog => dialog !== this.dialog)) return;
        requestAnimationFrame(() => {
          if (event.defaultPrevented || !this.isConnected || !this.dialog.open ||
              [...document.querySelectorAll('dialog:modal')].some(dialog => dialog !== this.dialog)) return;
          if (this.dialog.requestClose) this.dialog.requestClose();
          else if (this.dialog.dispatchEvent(new Event('cancel', {cancelable: true}))) this.dialog.close();
        });
      }, {signal});
      this.dialog.addEventListener('close', () => this.onClose(), {signal});
      this.dialog.addEventListener('click', event => {
        if (event.target !== this.dialog) return;
        const box = this.dialog.getBoundingClientRect();
        if (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom) this.dialog.close();
      }, {signal});
      this.worlds.forEach(world => world.addEventListener('toggle', () => {
        const summary = world.querySelector('summary');
        summary.setAttribute('aria-expanded', String(world.open));
        if (world.open) {
          this.worlds.filter(other => other !== world).forEach(other => { other.open = false; });
          this.dialog.dataset.activeWorld = world.dataset.world;
          this.updateTriggers();
          if (this.dialog.open && !this.mq.matches) requestAnimationFrame(() => {
            if (!world.open) return;
            const scroll = this.querySelector('.bp-nav__scroll');
            const top = scroll.scrollTop + world.getBoundingClientRect().top - scroll.getBoundingClientRect().top;
            scroll.scrollTo({top: Math.max(0, top), behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth'});
          });
        }
      }, {signal}));
      this.mq = matchMedia('(min-width: 1000px)');
      this.mq.addEventListener('change', () => {
        this.querySelector('.bp-nav__scroll').scrollTop = 0;
        if (this.dialog.open && this.mq.matches && !this.worlds.some(w => w.open && !w.hidden)) this.selectWorld('mystery');
        this.position();
      }, {signal});
      window.addEventListener('resize', () => { if (this.dialog.open) this.position(); }, {passive: true, signal});
      document.addEventListener('shopify:section:load', () => this.refreshAvailability(), {signal});
      document.addEventListener('visibilitychange', () => {
        clearTimeout(this.availabilityTimer);
        if (!document.hidden) this.refreshAvailability();
      }, {signal});
      this.refreshAvailability();
      this.applyWorldDestination();
      document.addEventListener('bp:gateway-ready', () => this.applyWorldDestination(), {signal});
      window.addEventListener('bp:universe-controller-ready', () => this.applyWorldDestination(), {signal});
      window.addEventListener('load', () => this.applyWorldDestination(), {once: true, signal});
      document.addEventListener('DOMContentLoaded', () => {
        this.refreshAvailability(); this.applyWorldDestination();
      }, {once: true, signal});
    }
    disconnectedCallback() {
      if (this.dialog?.open) this.dialog.close();
      this.abort?.abort();
      clearTimeout(this.availabilityTimer);
      document.documentElement.classList.remove('bp-nav-locked');
    }
    refreshAvailability() {
      let config = {};
      try { config = JSON.parse(document.querySelector('[data-bp-nav-world-config]')?.textContent || '{}'); } catch {}
      this.config = config;
      const now = Date.now();
      const start = Date.parse(config.starts), end = Date.parse(config.ends);
      const available = {lip: config.lip === true, event: config.event === true && now >= start && now < end};
      document.querySelectorAll('[data-bp-nav-available]').forEach(node => {
        node.hidden = !available[node.dataset.bpNavAvailable];
        if (node.hidden && node.open) node.open = false;
      });
      const title = this.querySelector('[data-bp-nav-event-title]');
      if (title) title.textContent = config.eventTitle || '';
      if (this.dialog.open && this.mq?.matches && !this.worlds.some(w => w.open && !w.hidden)) this.selectWorld('mystery');
      clearTimeout(this.availabilityTimer);
      const next = [start, end].filter(date => date > now).sort((a,b) => a-b)[0];
      if (next && !document.hidden) this.availabilityTimer = setTimeout(() => this.refreshAvailability(), Math.min(next-now+50, 2147483647));
    }
    selectWorld(key) {
      const selected = this.worlds.find(w => w.dataset.world === key && !w.hidden);
      if (!selected) return;
      this.worlds.forEach(w => { w.open = w === selected; w.querySelector('summary').setAttribute('aria-expanded', String(w.open)); });
      this.dialog.dataset.activeWorld = key;
      this.updateTriggers();
    }
    updateTriggers() {
      document.querySelectorAll('[aria-controls="wt-drawer-nav"]').forEach(trigger => {
        const expanded = this.dialog.open && (!trigger.dataset.bpNavOpen || trigger.dataset.bpNavOpen === this.dialog.dataset.activeWorld);
        trigger.setAttribute('aria-expanded', String(expanded));
      });
    }
    open(trigger, world) {
      this.returnFocus = trigger;
      this.refreshAvailability();
      if (world) this.selectWorld(world);
      else if (this.mq.matches) this.selectWorld(document.documentElement.dataset.bpUniverse || 'mystery');
      if (!this.dialog.open) this.dialog.showModal();
      this.position();
      document.documentElement.classList.add('bp-nav-locked');
      if (this.mq.matches && !this.worlds.some(w => w.open && !w.hidden)) this.selectWorld('mystery');
      this.updateTriggers();
      this.querySelector('[data-bp-nav-close]').focus({preventScroll: true});
    }
    position() {
      if (!this.mq.matches) {
        this.dialog.style.setProperty('--bp-nav-top', '0px');
        return;
      }
      const header = document.querySelector('page-header');
      const bottom = header?.getBoundingClientRect().bottom || 0;
      const top = Math.max(12, Math.min(bottom + 10, innerHeight - 400));
      this.dialog.style.setProperty('--bp-nav-top', top + 'px');
    }
    onClose() {
      document.documentElement.classList.remove('bp-nav-locked');
      this.updateTriggers();
      if (!this.handoffFocus && this.returnFocus?.isConnected && this.returnFocus.getClientRects().length) this.returnFocus.focus({preventScroll: true});
      this.handoffFocus = false;
    }
    handleClick(event) {
      const summary = event.target.closest('.bp-nav__world > summary');
      if (summary && this.mq.matches && summary.parentElement.open) {
        event.preventDefault(); return;
      }
      const trigger = event.target.closest('[data-bp-nav-open], .wt-header__menu-trigger, .wt-header__sticky-menu-trigger');
      if (trigger) {
        event.preventDefault(); event.stopImmediatePropagation();
        this.open(trigger, trigger.dataset.bpNavOpen); return;
      }
      if (event.target.closest('[data-bp-nav-close]')) { event.preventDefault(); this.dialog.close(); return; }
      if (event.target.closest('[data-bp-nav-search]')) {
        const searchTrigger = document.querySelector('.wt-header__search-trigger');
        if (searchTrigger) {
          event.preventDefault();
          this.handoffFocus = true;
          this.dialog.close();
          searchTrigger.click();
        }
        return;
      }
      const link = event.target.closest('[data-bp-nav-go]');
      if (link && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey && event.button === 0) {
        if (this.dataset.page === 'index' && window.BPUniverse && (link.dataset.bpNavGo !== 'lip' || window.BPLipLabAPI)) {
          event.preventDefault();
          const world = link.dataset.bpNavGo;
          this.handoffFocus = true;
          this.dialog.close();
          this.navigateWorld(world, link.dataset.bpNavLip);
        }
      }
    }
    navigateWorld(world, mode) {
      const target = world === 'lip' ? document.querySelector('[data-bp-lip-lab]') : document.querySelector('[data-bp-daily-event]');
      if (!target || !window.BPUniverse) return false;
      if (world === 'lip' && !window.BPLipLabAPI) return false;
      if (world === 'event' && target.dataset.bpEventValid !== 'true') return false;
      window.BPUniverse.setState(world, {persist:true, announce:true, transition:false, source:'world-navigation'});
      requestAnimationFrame(() => {
        if (world === 'lip') {
          const action = target.querySelector(mode === 'compose' ? '[data-lip-action="compose"]' : '[data-lip-action="quiz"]');
          if (action) {
            action.click();
            // The lab owns focus and scroll for the question or composer it opens.
            return;
          }
        }
        const heading = target.querySelector('h2');
        if (heading) { heading.setAttribute('tabindex','-1'); heading.focus({preventScroll:true}); }
        target.scrollIntoView({behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth', block:'start'});
      });
      return true;
    }
    applyWorldDestination() {
      if (this.destinationApplied || this.dataset.page !== 'index' || !window.BPUniverse?.isReady()) return;
      const params = new URLSearchParams(location.search);
      const world = params.get('bp_world');
      if (!['lip','event'].includes(world)) return;
      if (this.navigateWorld(world, params.get('bp_lip'))) this.destinationApplied = true;
    }
  }
  customElements.define('bp-world-navigation', BPWorldNavigation);
})();
